/**
 * Vault Types for SSP Enterprise App
 */

// Chain logo imports
import btcLogo from '@/assets/btc.svg';
import ltcLogo from '@/assets/ltc.svg';
import dogeLogo from '@/assets/doge.svg';
import rvnLogo from '@/assets/rvn.svg';
import fluxLogo from '@/assets/flux.svg';
import zecLogo from '@/assets/zec.svg';
import bchLogo from '@/assets/bch.svg';
import ethLogo from '@/assets/eth.svg';
import bscLogo from '@/assets/bsc.svg';
import avaxLogo from '@/assets/avax.svg';
import polLogo from '@/assets/pol.svg';
import baseLogo from '@/assets/base.svg';
import btcTestnetLogo from '@/assets/btcTestnet.svg';
import btcSignetLogo from '@/assets/btcSignet.svg';
import sepoliaLogo from '@/assets/ethTestnet.svg';
import amoyLogo from '@/assets/polTestnet.svg';
import fluxTestnetLogo from '@/assets/fluxTestnet.svg';
import solDevnetLogo from '@/assets/solDevnet.svg';
import solMainnetLogo from '@/assets/solMainnet.svg';

// ============================================================
// Status & Role Types
// ============================================================

export type VaultStatus = 'pending_setup' | 'active' | 'frozen' | 'archived';
export type VaultMemberRole = 'admin' | 'signer' | 'viewer';
export type EvmSigningMode = 'dual' | 'key_only' | 'wallet_only';
// Solana signing mode: dual = each SSP signer = wallet + key (2 ed25519 keys);
// single = each SSP signer = 1 ed25519 key (allows larger M-of-N up to 7-of-15).
export type SolSigningMode = 'sol_dual' | 'sol_single';
export type VaultSigningMode = EvmSigningMode | SolSigningMode;
export type XpubStatus = 'pending' | 'submitted' | 'not_required';
export type ProposalStatus =
  | 'pending'
  | 'pending_approval'
  | 'approved'
  | 'approved_pending_broadcast'
  | 'rejected'
  | 'cancelled'
  | 'broadcast'
  | 'confirmed'
  | 'expired'
  | 'failed'
  // Terminal: can never land (coins spent / EVM nonce used / account already
  // deployed). Not retryable — the user rebuilds it as a new proposal.
  | 'stale';
export type TxDirection = 'incoming' | 'outgoing' | 'self';
export type ExportFormat = 'csv' | 'xlsx' | 'pdf';
export type ThreatStatus = 'safe' | 'suspicious' | 'malicious';

export interface ThreatSignal {
  type: string;
  points: number;
  detail?: string;
}

export interface TokenThreatInfo {
  contract: string;
  chain: string;
  symbol: string;
  score: number;
  status: ThreatStatus;
  signals: ThreatSignal[];
  isOverride: boolean;
}

// ============================================================
// Vault List View Types
// ============================================================

export type VaultSortField = 'name' | 'chain' | 'createdAt' | 'status' | 'balanceUsd';
export type VaultSortOrder = 'asc' | 'desc';
export type VaultViewMode = 'grid' | 'list';

export interface NetworkFee {
  coin: string;
  base: number;
  economy: number;
  normal: number;
  fast: number;
  recommended: number;
}

export type VaultAuditEventType =
  | 'vault_created'
  | 'vault_activated'
  | 'vault_archived'
  | 'vault_unarchived'
  | 'vault_updated'
  | 'vault_frozen'
  | 'vault_unfrozen'
  | 'signer_xpub_submitted'
  | 'viewer_added'
  | 'viewer_removed'
  | 'member_promoted'
  | 'member_demoted'
  | 'address_generated'
  | 'tx_proposed'
  | 'tx_signed'
  | 'tx_rejected'
  | 'tx_broadcast'
  | 'tx_confirmed'
  | 'tx_failed'
  | 'tx_cancelled'
  | 'tx_expired'
  | 'tx_retry_broadcast'
  | 'tx_admin_approved'
  | 'tx_admin_rejected'
  | 'tx_timelock_cancelled'
  // Read-only share link for a proposal (payment status for the payee).
  | 'proposal_share_created'
  | 'proposal_share_revoked'
  | 'recurring_payment_created'
  | 'recurring_payment_updated'
  | 'recurring_payment_paused'
  | 'recurring_payment_resumed'
  | 'recurring_payment_cancelled'
  | 'recurring_payment_run_created'
  | 'recurring_payment_run_failed'
  | 'balance_synced'
  | 'settings_updated'
  | 'policy_updated'
  | 'whitelist_added'
  | 'whitelist_removed'
  | 'policy_rule_created'
  | 'policy_rule_updated'
  | 'policy_rule_deleted'
  | 'policy_rule_reordered';

// ============================================================
// Entity Interfaces
// ============================================================

export interface Vault {
  id: string;
  organizationId: string;
  name: string;
  description?: string;
  chain: string;
  vaultIndex: number;
  requiredSigners: number;
  totalSigners: number;
  status: VaultStatus;
  addressCount: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  archivedAt?: string;
  archivedBy?: string;
  frozenAt?: string;
  frozenBy?: string;
  frozenReason?: string;
  signingMode?: VaultSigningMode;
  tags?: string[];
  defaultDelegates?: string[];
  // Enrichment fields (populated by vault list)
  totalBalanceUsd?: number;
  memberCount?: number;
  lastActivityAt?: string;
}

export interface VaultSearchResult extends Vault {
  matchReason: 'name' | 'address' | 'transaction';
}

export interface VaultTagDefinition {
  id: string;
  name: string;
  description: string;
  color?: string;
  createdBy: string;
  createdAt: string;
}

export interface VaultDetail extends Vault {
  memberCount: number;
  pendingXpubCount: number;
}

export interface VaultMember {
  id: string;
  vaultId: string;
  organizationId: string;
  wkIdentity: string;
  role: VaultMemberRole;
  xpubStatus: XpubStatus;
  xpubWallet?: string;
  xpubKey?: string;
  xpubSubmittedAt?: string;
  addedBy: string;
  addedAt: string;
  removedAt?: string;
  removedBy?: string;
  email?: string;
  displayName?: string;
}

export interface VaultAddress {
  id: string;
  vaultId: string;
  chain: string;
  addressIndex: number;
  address: string;
  witnessScript?: string;
  redeemScript?: string;
  label?: string;
  createdAt: string;
  // Solana-only. Parent multisig PDA, sorted ed25519 member set, and ed25519
  // threshold for the on-chain program. Backend populates at derivation time.
  solanaMultisigPda?: string;
  solanaMembers?: string[];
  solanaThreshold?: number;
}

export interface TokenBalance {
  contract: string;
  symbol: string;
  name: string;
  balance: string;
  decimals: number;
  balanceUsd?: number;
  threatStatus?: ThreatStatus;
}

export interface VaultToken {
  contract: string;
  name: string;
  symbol: string;
  decimals: number;
  logo?: string;
}

export interface VaultBalance {
  id: string;
  vaultId: string;
  addressIndex: number;
  chain: string;
  address: string;
  balance: string;
  unconfirmedBalance: string;
  balanceUsd?: number;
  tokens?: TokenBalance[];
  /**
   * Outflow of proposals still in flight from this address, present only when
   * non-zero. `reserved` (UTXO): value of the coins those proposals hold —
   * NOT available to new proposals. `committed` (account chains): native
   * amount + fee and token amounts not yet broadcast — advisory only.
   */
  pendingOutflow?: {
    kind: 'reserved' | 'committed';
    /** Native coin, smallest unit. */
    amount: string;
    tokens?: Array<{ contract: string; amount: string }>;
    proposalIds: string[];
  };
  lastSyncedAt: string;
}

export interface VaultTransaction {
  id: string;
  vaultId: string;
  chain: string;
  txid: string;
  direction: TxDirection;
  fromAddresses?: string[];
  toAddresses?: string[];
  from?: string;
  to?: string;
  amount: string;
  fee?: string;
  amountUsd?: number;
  feeUsd?: number;
  rateUsd?: number;
  /**
   * How amountUsd was priced (relay): historical = coin rate at tx time,
   * stablecoin = $1, first_seen = live rate when first synced (approximate).
   * Absent on values synced before pricing was fixed — treat those as unreliable.
   */
  usdSource?: 'historical' | 'stablecoin' | 'first_seen';
  tokenContract?: string;
  tokenSymbol?: string;
  tokenDecimals?: number;
  confirmations: number;
  blockHeight?: number;
  timestamp: string;
  proposalId?: string;
  threatStatus?: ThreatStatus;
  addressPoisoning?: boolean;
  spamFlaggedBy?: string;
}

export interface FlaggedAddress {
  address: string;
  chain?: string;
  reason?: string;
  source: 'manual' | 'auto_poisoning';
  flaggedBy: string;
  flaggedAt: string;
}

export interface ProposalRecipient {
  address: string;
  amount: string;
}

/** One "add payment to a pending proposal" step (signatures were cleared). */
export interface ProposalAmendment {
  amendedBy: string;
  amendedAt: string;
  addedRecipients: ProposalRecipient[];
  previousRecipients: ProposalRecipient[];
  /** Signers whose signatures the amendment cleared. */
  previousSigners: Array<{ wkIdentity: string; signedAt: string }>;
}

/**
 * An in-flight proposal that blocks a new one: it reserves the coins
 * (FUNDS_RESERVED), occupies a Solana vault's single bundled slot
 * (VAULT_BUSY), or deploys the EVM account (VAULT_DEPLOYMENT_PENDING).
 */
export interface BlockingProposalInfo {
  id: string;
  status: ProposalStatus;
  createdAt: string;
  recipientCount: number;
  /** Sum of recipient amounts, smallest unit. */
  totalAmount: string;
  /** UTXO: value of the coins it holds at this address, smallest unit. */
  reservedAmount?: string;
  /** True when payments can be added to it ("add to pending proposal"). */
  amendable: boolean;
}

export interface ProposalInputDetail {
  index: number;
  addressIndex: number;
  witnessScript?: string;
  redeemScript?: string;
  amount: string;
}

// ============================================================
// Transaction Simulation (advisory, never gates signing)
// ============================================================
//
// READ-ONLY, ADVISORY layer. Simulation is comprehension + early-warning ONLY;
// it NEVER gates signing or broadcast. Enforcement stays in vaultPolicyService.
// A simulation error/timeout/unavailable must leave the proposal fully signable.
// The enum below is MIRRORED VERBATIM from
// ssp-relay-enterprise/src/types/vault.ts — keep them identical across repos.
// ============================================================

export type SimWarningSeverity = 'critical' | 'high' | 'medium' | 'info';

export type SimWarningCode =
  // approvals
  | 'UNLIMITED_APPROVAL' // critical — approve()/setApprovalForAll with max/unbounded allowance
  | 'NON_ZERO_APPROVAL' // high — any token spend approval to a non-allowlisted spender
  | 'APPROVAL_TO_EOA' // high — approving an EOA (not a contract) is almost always a scam
  // recipients
  | 'RECIPIENT_NOT_ALLOWLISTED' // medium — recipient not in contacts/whitelist (info if whitelist disabled)
  | 'ADDRESS_POISONING' // critical — recipient look-alike of a known address (prefix+suffix match)
  | 'NEW_RECIPIENT' // info — vault has never sent to this address before
  // contract risk
  | 'UNVERIFIED_CONTRACT' // medium — destination contract source unverified / no known ABI
  | 'NEW_CONTRACT' // medium — contract deployed very recently (< 7d)
  | 'VALUE_TO_CONTRACT' // info — native value sent to a contract (may be intended)
  | 'KNOWN_MALICIOUS' // critical — provider flags address/contract as malicious
  // execution
  | 'SIMULATION_REVERTED' // high — tx reverts in simulation; will fail on-chain (waste fee / stuck nonce)
  | 'BALANCE_MISMATCH' // high — simulated outflow != proposal's stated recipients/amounts
  | 'SIMULATION_DECODE_MISMATCH' // critical — server sim disagrees with device's own trustless decode
  // degradation
  | 'SIMULATION_UNAVAILABLE'; // info — could not simulate; preview is best-effort

export interface SimWarning {
  code: SimWarningCode;
  severity: SimWarningSeverity;
  message: string; // English only; i18n key on the client
  detail?: string; // e.g. the look-alike address, the spender, the revert reason
  provider?: string; // which engine raised it
}

/**
 * One asset's before/after for the SOURCE smart account / vault address.
 * All amounts are smallest-unit decimal strings (wei/satoshi/token base unit).
 */
export interface SimBalanceChange {
  asset: string; // 'native' | erc20 contract | token symbol
  symbol: string;
  decimals: number;
  address?: string; // contract address for tokens
  beforeRaw: string; // smallest unit
  afterRaw: string; // smallest unit (before - out - fee, etc.)
  deltaRaw: string; // signed, smallest unit
  deltaUsd?: number;
  direction: 'out' | 'in';
}

/**
 * Decoded top-level call (EVM) for "what this does in English". Args are
 * stringified for transport. Carries NO secrets.
 */
export interface SimDecodedCall {
  standard?: 'erc20' | 'erc721' | 'erc1155' | 'router' | 'entrypoint' | 'unknown';
  method?: string; // 'transfer' | 'approve' | 'setApprovalForAll' | ...
  args?: Record<string, string>; // stringified for transport (e.g. spender, amount)
  target?: string; // contract being called
  targetVerified?: boolean;
}

/**
 * Embedded subdocument on the proposal. No new collection — it travels with the
 * proposal in every GET/sync/notification. Carries no signing-sensitive material
 * (no signatures/nonces/signed-hex). Advisory only — never gates signing.
 */
export interface ProposalSimulation {
  status: 'pending' | 'ok' | 'reverted' | 'unavailable';
  provider?: string; // e.g. 'self_hosted', 'utxo_native'
  simulatedAt?: string; // ISO; drives staleness
  chainStateBlock?: number; // EVM: block the sim ran against (staleness signal)
  balanceChanges: SimBalanceChange[];
  decodedCall?: SimDecodedCall;
  warnings: SimWarning[];
  revertReason?: string;
}

export interface ReservedNonceForSigner {
  wkIdentity: string;
  walletNonce: { kPublic: string; kTwoPublic: string };
  keyNonce: { kPublic: string; kTwoPublic: string };
}

// Solana approval-flow selection. `bundled` packs all approvals + execute into
// one durable-nonce tx (small M). `split` lands one tx per SSP signer + a
// paymaster-only execute tx (large M, up to N ≤ 15). See SOLANA_SPLIT_APPROVAL_PLAN.md §2.
export type SolanaFlow = 'bundled' | 'split';

// Per-signer on-chain status for a split-flow proposal. Mirrors a backend
// solanaSplit.signerTxs entry MINUS that signer's unsignedTxBase64 and raw
// signatures (privacy — the signer fetches their own payload on demand).
export type SolanaSignerTxStatus =
  | 'built'
  | 'held'
  | 'broadcast'
  | 'confirmed'
  | 'failed'
  | 'superseded';

export interface SolanaSignerTxView {
  wkIdentity: string;
  kind: 'create' | 'approve';
  status: SolanaSignerTxStatus;
  txid?: string;
  failureReason?: string;
}

// Signer-visible mirror of the backend solanaSplit subdocument. Deliberately
// omits other signers' unsignedTxBase64 / raw signatures (§7).
export interface SolanaSplitView {
  transactionIndex?: string;
  transactionAccount?: string;
  indexLocked?: boolean;
  creatorWk?: string;
  holdFinalApproval?: boolean;
  /** HD address index of the source vault address for this proposal. */
  addressIndex?: number;
  signerTxs?: SolanaSignerTxView[];
  executeTxid?: string;
  paymasterSpendLamports?: string;
}

// Per-signer signing payload returned by the solana-signing-payload endpoint.
// `kind` tells the app whether this signer is the creator (carries
// create_transaction) or an approver. The app passes unsignedTxBase64 to the
// wallet as rawUnsignedTx and synthesizes inputDetails from addressIndex (§7).
export interface SolanaSigningPayload {
  unsignedTxBase64: string;
  signerSlots: Array<{
    wkIdentity: string;
    role: 'wallet' | 'key';
    position: number;
    pubkey: string;
  }>;
  addressIndex: number;
  kind: 'create' | 'approve';
}

export interface SignerPubKeys {
  wkIdentity: string;
  walletPubKey: string;
  keyPubKey: string;
}

/**
 * Fully signed transaction ready for manual submission.
 * - utxo_hex: raw hex for any node's sendrawtransaction / blockbook sendtx
 * - evm_userop: ERC-4337 UserOperation (signature filled) for eth_sendUserOperation
 */
export interface FinalSignedTx {
  format: 'utxo_hex' | 'evm_userop';
  rawHex?: string;
  userOp?: Record<string, string>;
  entryPoint?: string;
  assembledAt: string;
}

/** Snapshot of a transfer's USD value at a point in time (relay-computed). */
export interface UsdValuation {
  amountUsd: number;
  feeUsd?: number;
  /** USD price of the transferred asset used. */
  assetRateUsd: number;
  nativeRateUsd?: number;
  source: 'spot' | 'historical' | 'stablecoin';
  /** The moment the value refers to. */
  pricedAt: string;
  capturedAt: string;
}

/** One proposal in the org-wide activity feed. */
export interface OrgProposalActivityItem {
  proposal: TxProposal;
  vaultId: string;
  vaultName: string;
}

/** Org-wide proposal activity across all vaults the caller can access. */
export interface OrgProposalActivity {
  days: number;
  since: string;
  pending: OrgProposalActivityItem[];
  recentlySigned: OrgProposalActivityItem[];
  recentlySent: OrgProposalActivityItem[];
  eligibleSigners: Record<string, string[]>;
}

/** A pending proposal awaiting the current user's signature (cross-vault list). */
export interface SigningRequest {
  proposal: TxProposal;
  vaultId: string;
  vaultName: string;
  /** Vault's active signer/admin identities, for the "waiting on" display. */
  eligibleSigners?: string[];
}

export interface TxProposal {
  id: string;
  vaultId: string;
  organizationId: string;
  chain: string;
  proposedBy: string;
  recipients: ProposalRecipient[];
  fee?: string;
  memo?: string;
  rawUnsignedTx?: string;
  inputDetails?: ProposalInputDetail[];
  orgIndex?: number;
  vaultIndex?: number;
  // UTXO progressive signing: accumulated TX hex with SIGHASH signatures
  currentSignedHex?: string;
  // Solana: bundled signing tx + slot map, lazy-built on first sign request.
  // Absent on UTXO/EVM proposals AND on split-flow Solana proposals.
  solanaBundle?: {
    unsignedTxBase64: string;
    paymasterSlot: number;
    signerSlots: Array<{
      wkIdentity: string;
      role: 'wallet' | 'key';
      position: number;
      pubkey: string;
    }>;
    recentBlockhash: string;
    nonceAccount: string;
    /** HD address index the wallet+key must derive their keypair at. */
    addressIndex: number;
    builtAt: string;
  };
  // Solana flow selector. `split` proposals carry solanaSplit (not solanaBundle)
  // and require a per-signer payload fetch before signing (§7).
  solanaFlow?: SolanaFlow;
  // Solana split-flow per-signer approval state (signer-visible mirror).
  solanaSplit?: SolanaSplitView;
  // Solana inner-message metadata, built at create for BOTH flows. Exposes the
  // source addressIndex so the source-address fallback chain resolves correctly
  // for split proposals at addressIndex > 0 (§7).
  solanaProposal?: {
    addressIndex?: number;
  };
  requiredSignatures: number;
  signatureCount: number;
  rejectionCount: number;
  signatures: Array<{
    wkIdentity: string;
    signedAt: string;
  }>;
  rejections: Array<{
    wkIdentity: string;
    reason?: string;
    rejectedAt: string;
  }>;
  status: ProposalStatus;
  signingMode?: VaultSigningMode;
  reservedNonces?: ReservedNonceForSigner[];
  designatedSigners?: string[];
  signerPubKeys?: SignerPubKeys[];
  // ERC-20 token fields (present when proposal sends a token, absent for native currency)
  tokenContract?: string;
  tokenSymbol?: string;
  tokenDecimals?: number;
  // EVM native transfers: hex data included in execute() call
  customData?: string;
  // Simulation result (advisory, never gates signing). Deserialized inline from
  // getProposal; patched server-side asynchronously after creation.
  simulation?: ProposalSimulation;
  // Policy evaluation result (present when vault has active policies)
  policyCheck?: {
    status: 'compliant' | 'requires_approval' | 'blocked';
    violations: PolicyViolation[];
    evaluatedAt: string;
    amountUsd: number;
    // Ordered-rules engine trace (absent when the vault has zero enabled rules)
    evaluatedRules?: RuleEvaluationTrace[];
    matchedRuleId?: string;
    // P6 — compliance/AML screening result (present only when the org has a
    // compliance provider configured & enabled and screening ran). Runs AFTER
    // rules, tighten-only. Coordination-layer signal — never custody enforcement.
    complianceCheck?: ProposalComplianceCheck;
  };
  // Admin approval (when required by policy)
  adminApproval?: {
    status: 'pending' | 'approved' | 'rejected';
    approvedBy?: string;
    approvedAt?: string;
    rejectedBy?: string;
    rejectedAt?: string;
    rejectionReason?: string;
  };
  // Multi-round approval workflow (Phase 2) — present only on
  // workflow-bearing proposals, mutually exclusive with adminApproval
  approvalWorkflow?: ProposalApprovalWorkflow;
  // Time lock (when triggered by policy)
  timeLock?: {
    scheduledBroadcastAt: string;
    cancelledBy?: string;
    cancelledAt?: string;
  };
  // Full EVM UserOp struct (for trustless decode on wallet/key)
  evmUserOp?: Record<string, string>;
  // EVM gas details (derived from stored evmUserOp, for display only)
  evmGasDetails?: {
    maxFeePerGas: string;
    maxPriorityFeePerGas: string;
    callGasLimit: string;
    verificationGasLimit: string;
    preVerificationGas: string;
    sender: string;
    nonce: string;
    hasInitCode: boolean;
  };
  broadcastTxid?: string;
  /** Fully signed, network-ready transaction (persisted by the relay before broadcasting). */
  finalSignedTx?: FinalSignedTx;
  /** USD value when the proposal was created. */
  valueUsdAtCreation?: UsdValuation;
  /** USD value when the transaction was broadcast. */
  valueUsdAtBroadcast?: UsdValuation;
  /** Set when the proposal was created by a recurring payment schedule. */
  recurringPaymentId?: string;
  /** EVM: hash of the on-chain bundle tx (broadcastTxid is the ERC-4337 userOpHash). */
  onChainTxHash?: string;
  /** EVM: false when the UserOp was mined but its execution reverted. */
  executionSuccess?: boolean;
  broadcastAt?: string;
  confirmedAt?: string;
  failureReason?: string;
  /** Status `stale`: why the proposal can never land (human-readable). */
  staleReason?: string;
  staleDetectedAt?: string;
  /** This proposal was rebuilt from that (stale/cancelled/…) proposal. */
  rebuiltFrom?: string;
  /** This proposal was rebuilt as that proposal. */
  supersededBy?: string;
  /** EVM: the proposal's own 2D-nonce lane (hex key, nonce >> 64). */
  evmNonceKey?: string;
  /** EVM: the lane was taken over from this (dead) proposal. */
  evmLaneReusedFrom?: string;
  /** UTXO: payments added while pending (each one cleared the signatures). */
  amendCount?: number;
  amendHistory?: ProposalAmendment[];
  createdAt: string;
  expiresAt: string;
  updatedAt: string;
}

export interface PendingSetup {
  vaultId: string;
  vaultName: string;
  organizationId: string;
  organizationName: string;
  chain: string;
  orgIndex: number;
  vaultIndex: number;
  requiredSigners: number;
  totalSigners: number;
}

export interface VaultBalanceHistory {
  id: string;
  vaultId: string;
  chain: string;
  totalBalance: string;
  totalBalanceUsd?: number;
  rateUsd?: number;
  addressBalances: Array<{
    addressIndex: number;
    balance: string;
  }>;
  snapshotAt: string;
}

export interface VaultAuditLog {
  id: string;
  vaultId: string;
  organizationId: string;
  eventType: VaultAuditEventType;
  actorWkIdentity: string;
  targetWkIdentity?: string;
  details: Record<string, unknown>;
  createdAt: string;
  ip?: string;
  userAgent?: string;
  vaultName?: string;
}

// ============================================================
// Request Types
// ============================================================

export interface CreateVaultRequest {
  name: string;
  description?: string;
  chain: string;
  requiredSigners: number;
  signers: string[];
  viewers?: string[];
  signingMode?: VaultSigningMode;
}

export interface UpdateVaultRequest {
  name?: string;
  description?: string;
  tags?: string[];
}

export interface SubmitXpubRequest {
  xpubWallet: string;
  xpubKey: string;
  walletXpubSignature: string;
  keyXpubSignature: string;
}

export interface CreateProposalRequest {
  recipients: ProposalRecipient[];
  fee?: string;
  feePerByte?: number;
  gasPrice?: string; // EVM: maxFeePerGas in wei (overrides fee-based derivation)
  maxPriorityFeePerGas?: string; // EVM: maxPriorityFeePerGas in wei (tip only, separate from gasPrice)
  gasLimit?: number; // EVM: total gas limit (preVerification + call + verification)
  // EVM: individual gas components (override dynamic estimation when all three provided)
  callGasLimit?: number;
  verificationGasLimit?: number;
  preVerificationGas?: number;
  memo?: string;
  sourceAddressIndex?: number;
  designatedSigners?: string[];
  // ERC-20 token fields (EVM only, omit for native currency)
  tokenContract?: string;
  tokenSymbol?: string;
  tokenDecimals?: number;
  // EVM native transfers: hex data for execute() call (e.g. smart contract interaction)
  customData?: string;
  // Fee estimation: when true, compute the maximum sendable amount
  sendAll?: boolean;
  // Rebuild: id of a stale/cancelled/expired/rejected proposal of the same
  // vault this one replaces (the original gets `supersededBy`).
  rebuildOf?: string;
  /** Created from this recurring payment's "Create proposal" (manual run). */
  recurringPaymentId?: string;
}

export interface SignProposalRequest {
  walletSignatures: string[];
  keySignatures: string[];
  walletPubKey: string;
  keyPubKey: string;
  // UTXO progressive signing: fully signed TX hex from wallet+key pair
  signedHex?: string;
}

export interface RejectProposalRequest {
  reason?: string;
}

// ============================================================
// Signature Requests (WalletConnect Phase 2 — vault message signing)
// A non-transaction, non-broadcasting M-of-N signature over a dApp message.
// ============================================================

export type SignatureRequestStatus = 'pending' | 'signed' | 'rejected' | 'expired';

export type VaultMessageType = 'personal_sign' | 'eth_signTypedData_v4';

export interface SignatureRequest {
  id: string;
  vaultId: string;
  organizationId: string;
  chain: string;
  requestedBy: string;
  messageType: VaultMessageType;
  rawMessage: string;
  displayMessage: string;
  /** 0x 32-byte EIP-191 digest the signers sign (also the ERC-1271 hash). */
  digest: string;
  dappOrigin?: string;
  sourceAddressIndex: number;
  vaultAddress: string;
  vaultIndex?: number;
  requiredSignatures: number;
  signatureCount: number;
  rejectionCount: number;
  signingMode?: VaultSigningMode;
  designatedSigners: string[];
  signerPubKeys?: SignerPubKeys[];
  reservedNonces?: ReservedNonceForSigner[];
  signatures: Array<{ wkIdentity: string; signedAt: string }>;
  rejections: Array<{ wkIdentity: string; reason?: string; rejectedAt: string }>;
  status: SignatureRequestStatus;
  /** 128-byte ABI-encoded Schnorr blob, present once status === 'signed'. */
  finalSignature?: string;
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
}

export interface CreateSignatureRequestRequest {
  message: string;
  messageType?: VaultMessageType;
  dappOrigin?: string;
  sourceAddressIndex?: number;
  designatedSigners?: string[];
}

export interface SignSignatureRequestRequest {
  walletSignatures: string[];
  keySignatures: string[];
  walletPubKey: string;
  keyPubKey: string;
}

// ============================================================
// Response Wrappers
// ============================================================

export interface VaultResponse {
  success: boolean;
  vault?: Vault;
  error?: string;
  errorCode?: string;
}

export interface VaultDetailResponse {
  success: boolean;
  vault?: VaultDetail;
  error?: string;
  errorCode?: string;
}

export interface VaultsListResponse {
  success: boolean;
  vaults?: Vault[];
  error?: string;
}

export interface VaultMembersListResponse {
  success: boolean;
  members?: VaultMember[];
  error?: string;
}

export interface VaultAddressesListResponse {
  success: boolean;
  addresses?: VaultAddress[];
  error?: string;
}

export interface VaultAddressResponse {
  success: boolean;
  address?: VaultAddress;
  error?: string;
  errorCode?: string;
}

export interface VaultBalancesResponse {
  success: boolean;
  balances?: VaultBalance[];
  totalBalance?: string;
  totalBalanceUsd?: number;
  error?: string;
}

export interface VaultTransactionsResponse {
  success: boolean;
  transactions?: VaultTransaction[];
  total?: number;
  error?: string;
}

export interface VaultBalanceHistoryResponse {
  success: boolean;
  history?: VaultBalanceHistory[];
  error?: string;
}

/**
 * Compact balance-history point returned by the org portfolio aggregate.
 * The portfolio chart only reads snapshot time + totals; the per-address
 * breakdown of VaultBalanceHistory is deliberately absent to keep the
 * single aggregate response small. Full VaultBalanceHistory is structurally
 * assignable to this type, so the legacy per-vault fallback fits too.
 */
export interface PortfolioHistoryPoint {
  snapshotAt: string;
  totalBalance: string;
  totalBalanceUsd?: number;
}

export interface OrgPortfolioVaultData {
  vaultId: string;
  balances: VaultBalance[];
  totalBalance: string;
  totalBalanceUsd: number;
  transactions: VaultTransaction[];
  /** Latest outgoing + internal transfers. Absent on relays predating the field. */
  outgoingTransactions?: VaultTransaction[];
  pendingProposals: TxProposal[];
  history: PortfolioHistoryPoint[];
}

export interface OrgPortfolioResponse {
  success: boolean;
  vaults?: OrgPortfolioVaultData[];
  error?: string;
}

export interface ProposalResponse {
  success: boolean;
  proposal?: TxProposal;
  error?: string;
  errorCode?: string;
  /** FUNDS_RESERVED / VAULT_BUSY / VAULT_DEPLOYMENT_PENDING: what blocks it. */
  blockingProposals?: BlockingProposalInfo[];
}

export interface FeeEstimateResponse {
  success: boolean;
  fee?: string;
  feeRate?: number;
  inputCount?: number;
  outputCount?: number;
  txSize?: number;
  changeAmount?: string;
  maxAmount?: string;
  isAccountCreation?: boolean;
  gasLimit?: number;
  callGasLimit?: number;
  verificationGasLimit?: number;
  preVerificationGas?: number;
  maxFeePerGas?: string;
  maxPriorityFeePerGas?: string;
  error?: string;
  errorCode?: string;
  /** FUNDS_RESERVED / VAULT_BUSY / VAULT_DEPLOYMENT_PENDING: what blocks it. */
  blockingProposals?: BlockingProposalInfo[];
}

export interface ProposalsListResponse {
  success: boolean;
  proposals?: TxProposal[];
  total?: number;
  error?: string;
}

export interface PendingSetupsResponse {
  success: boolean;
  setups?: PendingSetup[];
  error?: string;
}

export interface VaultAuditLogsResponse {
  success: boolean;
  logs?: VaultAuditLog[];
  total?: number;
  error?: string;
}

export interface SimpleVaultResponse {
  success: boolean;
  error?: string;
  errorCode?: string;
}

// ============================================================
// Display Helpers
// ============================================================

export const vaultStatusLabels: Record<VaultStatus, string> = {
  pending_setup: 'Pending Setup',
  active: 'Active',
  frozen: 'Frozen',
  archived: 'Archived',
};

export const vaultStatusColors: Record<VaultStatus, string> = {
  pending_setup: 'orange',
  active: 'green',
  frozen: 'red',
  archived: 'default',
};

export const proposalStatusLabels: Record<ProposalStatus, string> = {
  pending: 'Pending',
  pending_approval: 'Awaiting Approval',
  approved: 'Approved',
  approved_pending_broadcast: 'Time-Locked',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  broadcast: 'Sent',
  confirmed: 'Confirmed',
  expired: 'Expired',
  failed: 'Failed',
  stale: 'Stale',
};

export const proposalStatusColors: Record<ProposalStatus, string> = {
  pending: 'blue',
  pending_approval: 'orange',
  approved: 'cyan',
  approved_pending_broadcast: 'purple',
  rejected: 'red',
  cancelled: 'default',
  broadcast: 'green',
  confirmed: 'green',
  expired: 'default',
  failed: 'red',
  stale: 'volcano',
};

export const vaultAuditEventLabels: Record<VaultAuditEventType, string> = {
  vault_created: 'Vault Created',
  vault_activated: 'Vault Activated',
  vault_archived: 'Vault Archived',
  vault_unarchived: 'Vault Unarchived',
  vault_updated: 'Vault Updated',
  vault_frozen: 'Vault Frozen',
  vault_unfrozen: 'Vault Unfrozen',
  signer_xpub_submitted: 'Signer Keys Submitted',
  viewer_added: 'Viewer Added',
  viewer_removed: 'Viewer Removed',
  member_promoted: 'Member Promoted to Admin',
  member_demoted: 'Admin Demoted to Signer',
  address_generated: 'Address Generated',
  tx_proposed: 'Transaction Proposed',
  tx_signed: 'Transaction Signed',
  tx_rejected: 'Transaction Rejected',
  tx_broadcast: 'Transaction Sent',
  tx_confirmed: 'Transaction Confirmed',
  tx_failed: 'Transaction Failed',
  tx_cancelled: 'Transaction Cancelled',
  tx_expired: 'Transaction Expired',
  tx_retry_broadcast: 'Transaction Resent',
  tx_admin_approved: 'Admin Approved Proposal',
  tx_admin_rejected: 'Admin Rejected Proposal',
  tx_timelock_cancelled: 'Time-Lock Cancelled',
  proposal_share_created: 'Share Link Created',
  proposal_share_revoked: 'Share Link Revoked',
  recurring_payment_created: 'Recurring Payment Created',
  recurring_payment_updated: 'Recurring Payment Updated',
  recurring_payment_paused: 'Recurring Payment Paused',
  recurring_payment_resumed: 'Recurring Payment Resumed',
  recurring_payment_cancelled: 'Recurring Payment Cancelled',
  recurring_payment_run_created: 'Recurring Proposal Created',
  recurring_payment_run_failed: 'Recurring Run Failed',
  balance_synced: 'Balance Synced',
  settings_updated: 'Settings Updated',
  policy_updated: 'Policy Updated',
  whitelist_added: 'Whitelist Address Added',
  whitelist_removed: 'Whitelist Address Removed',
  policy_rule_created: 'Policy Rule Created',
  policy_rule_updated: 'Policy Rule Updated',
  policy_rule_deleted: 'Policy Rule Deleted',
  policy_rule_reordered: 'Policy Rules Reordered',
};

export const vaultAuditEventColors: Record<VaultAuditEventType, string> = {
  vault_created: 'green',
  vault_activated: 'green',
  vault_archived: 'default',
  vault_unarchived: 'green',
  vault_updated: 'blue',
  vault_frozen: 'red',
  vault_unfrozen: 'green',
  signer_xpub_submitted: 'cyan',
  viewer_added: 'green',
  viewer_removed: 'orange',
  member_promoted: 'purple',
  member_demoted: 'orange',
  address_generated: 'blue',
  tx_proposed: 'blue',
  tx_signed: 'cyan',
  tx_rejected: 'red',
  tx_broadcast: 'orange',
  tx_confirmed: 'green',
  tx_failed: 'red',
  tx_cancelled: 'default',
  tx_expired: 'default',
  tx_retry_broadcast: 'orange',
  tx_admin_approved: 'green',
  tx_admin_rejected: 'red',
  tx_timelock_cancelled: 'orange',
  proposal_share_created: 'cyan',
  proposal_share_revoked: 'orange',
  recurring_payment_created: 'blue',
  recurring_payment_updated: 'blue',
  recurring_payment_paused: 'orange',
  recurring_payment_resumed: 'green',
  recurring_payment_cancelled: 'default',
  recurring_payment_run_created: 'cyan',
  recurring_payment_run_failed: 'red',
  balance_synced: 'blue',
  settings_updated: 'purple',
  policy_updated: 'purple',
  whitelist_added: 'green',
  whitelist_removed: 'orange',
  policy_rule_created: 'green',
  policy_rule_updated: 'purple',
  policy_rule_deleted: 'orange',
  policy_rule_reordered: 'purple',
};

export const vaultMemberRoleLabels: Record<VaultMemberRole, string> = {
  admin: 'Admin',
  signer: 'Signer',
  viewer: 'Viewer',
};

export const vaultMemberRoleColors: Record<VaultMemberRole, string> = {
  admin: 'purple',
  signer: 'blue',
  viewer: 'green',
};

export const txDirectionLabels: Record<TxDirection, string> = {
  incoming: 'Received',
  outgoing: 'Sent',
  self: 'Self',
};

export const txDirectionColors: Record<TxDirection, string> = {
  incoming: 'green',
  outgoing: 'red',
  self: 'blue',
};

const txDirectionPendingLabels: Record<TxDirection, string> = {
  incoming: 'Receiving',
  outgoing: 'Sending',
  self: 'Self (pending)',
};

export function txDirectionLabel(direction: TxDirection, confirmations: number): string {
  return confirmations > 0 ? txDirectionLabels[direction] : txDirectionPendingLabels[direction];
}

export function txDirectionColor(direction: TxDirection, confirmations: number): string {
  // Unconfirmed — render as amber/gold so users can immediately tell it's pending.
  return confirmations > 0 ? txDirectionColors[direction] : 'gold';
}

/** Format a number as USD with adaptive precision based on magnitude */
export function formatUsd(value: number): string {
  const abs = Math.abs(value);
  let maxFrac = 2;
  if (abs > 0 && abs < 0.0001) maxFrac = 8;
  else if (abs < 0.01) maxFrac = 6;
  else if (abs < 1) maxFrac = 4;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: maxFrac,
  }).format(value);
}

/**
 * Convert raw balance string (smallest unit) to a human-readable number.
 * Uses string manipulation to avoid IEEE 754 precision loss.
 */
export function rawToHuman(rawBalance: string, decimals: number): number {
  // Untrusted metadata (ENTX-A-2): never scale by a non-integer/out-of-range
  // decimals value or a non-integer amount — report "no value" instead.
  if (!isSafeDecimals(decimals) || !/^\d*$/.test(rawBalance || '0')) return NaN;
  const s = (rawBalance || '0').replace(/^0+/, '') || '0';
  if (decimals === 0) return Number(s);
  if (s.length <= decimals) {
    return parseFloat(`0.${s.padStart(decimals, '0')}`);
  }
  const intPart = s.slice(0, s.length - decimals);
  const fracPart = s.slice(s.length - decimals);
  return parseFloat(`${intPart}.${fracPart}`);
}

/** Convert raw balance (smallest unit) to USD using crypto rates */
export function computeBalanceUsd(
  rawBalance: string,
  chain: string,
  cryptoRates: Record<string, number>
): number {
  const decimals = chainDecimals[chain];
  const rate = cryptoRates[chain];
  if (decimals === undefined || !rate) return 0;
  const usd = rawToHuman(rawBalance, decimals) * rate;
  return Number.isFinite(usd) ? usd : 0;
}

export interface ExpiryInfo {
  /** Human label, e.g. "Expires in 3h", "Expires in 2d", or "Expired". */
  label: string;
  /** antd Tag color reflecting urgency. */
  color: 'red' | 'orange' | 'default';
  isExpired: boolean;
}

/**
 * Describe how soon a pending proposal expires, with an urgency color.
 * Red ≤ 3h or expired, orange ≤ 24h, default otherwise. Lets co-signers see
 * "act now" at a glance instead of discovering an expired proposal too late.
 */
export function getExpiryInfo(expiresAt: string, now: number = Date.now()): ExpiryInfo {
  const ms = new Date(expiresAt).getTime() - now;
  if (Number.isNaN(ms)) return { label: '', color: 'default', isExpired: false };
  if (ms <= 0) return { label: 'Expired', color: 'red', isExpired: true };
  const mins = Math.floor(ms / 60_000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  let label: string;
  if (days >= 1) label = `Expires in ${String(days)}d`;
  else if (hours >= 1) label = `Expires in ${String(hours)}h`;
  else label = `Expires in ${String(Math.max(1, mins))}m`;
  const color: ExpiryInfo['color'] =
    ms <= 3 * 3_600_000 ? 'red' : ms <= 24 * 3_600_000 ? 'orange' : 'default';
  return { label, color, isExpired: false };
}

// EVM chain detection
const EVM_CHAINS = new Set(['eth', 'bsc', 'avax', 'polygon', 'base', 'sepolia', 'amoy']);
const SOL_CHAINS = new Set(['solDevnet', 'solMainnet']);

/** Check if a chain is EVM-based (requires enterprise nonces for signing) */
export function isEvmChain(chain: string): boolean {
  return EVM_CHAINS.has(chain);
}

/** Check if a chain is Solana-based (uses ed25519 multisig program). */
export function isSolanaChain(chain: string): boolean {
  return SOL_CHAINS.has(chain);
}

/**
 * Narrow VaultSigningMode to EvmSigningMode for EVM/UTXO call sites.
 * Returns undefined for Solana modes — callers should branch on chain first.
 */
export function asEvmSigningMode(mode: VaultSigningMode | undefined): EvmSigningMode | undefined {
  if (mode === undefined) return undefined;
  if (mode === 'dual' || mode === 'key_only' || mode === 'wallet_only') {
    return mode;
  }
  return undefined;
}

// Chain decimal precision (smallest unit → display unit)
export const chainDecimals: Record<string, number> = {
  btc: 8,
  ltc: 8,
  doge: 8,
  rvn: 8,
  flux: 8,
  zec: 8,
  bch: 8,
  eth: 18,
  bsc: 18,
  avax: 18,
  polygon: 18,
  base: 18,
  btcTestnet: 8,
  btcSignet: 8,
  sepolia: 18,
  amoy: 18,
  fluxTestnet: 8,
  solDevnet: 9,
  solMainnet: 9,
};

/**
 * Decimals accepted for scaling base units: an integer in [0, 36]. Token
 * metadata is untrusted input — `10n ** BigInt(-1)` or `BigInt(1.5)` would
 * throw during render and take the whole page down (ENTX-A-2).
 */
function isSafeDecimals(decimals: unknown): decimals is number {
  return (
    typeof decimals === 'number' && Number.isInteger(decimals) && decimals >= 0 && decimals <= 36
  );
}

/** Parse a base-unit integer string; null for anything else (never throws). */
function parseBaseUnitString(balance: string): bigint | null {
  const value = balance || '0';
  return /^\d{1,78}$/.test(value) ? BigInt(value) : null;
}

/** Convert balance from smallest unit (satoshis/wei) to display unit */
export function formatBalance(balance: string, chain: string): string {
  const decimals = chainDecimals[chain];
  if (decimals === undefined) return balance;
  const raw = parseBaseUnitString(balance);
  if (raw === null) return balance;
  const divisor = 10n ** BigInt(decimals);
  const whole = raw / divisor;
  const remainder = raw % divisor;
  if (remainder === 0n) return whole.toString();
  const fracStr = remainder.toString().padStart(decimals, '0').replace(/0+$/, '');
  return `${whole.toString()}.${fracStr}`;
}

/** Convert display amount (e.g. "0.5" BTC) to smallest unit string (e.g. "50000000" satoshis).
 *  Inverse of formatBalance. Returns null if the input is not a valid positive number. */
export function parseAmount(displayAmount: string, chain: string): string | null {
  const decimals = chainDecimals[chain];
  if (decimals === undefined) return null;

  const trimmed = displayAmount.trim();
  if (!trimmed || trimmed === '-') return null;

  // Split into whole and fractional parts
  const parts = trimmed.split('.');
  if (parts.length > 2) return null;

  const wholePart = parts[0] || '0';
  const fracPart = parts[1] || '';

  // Validate characters (digits only, no leading minus)
  if (!/^\d+$/.test(wholePart)) return null;
  if (fracPart && !/^\d+$/.test(fracPart)) return null;

  // Truncate fractional part to max decimals (no rounding — matches blockchain precision)
  const truncatedFrac = fracPart.slice(0, decimals).padEnd(decimals, '0');

  const raw = BigInt(wholePart) * 10n ** BigInt(decimals) + BigInt(truncatedFrac);
  if (raw <= 0n) return null;

  return raw.toString();
}

/** Convert display amount to smallest unit string using explicit decimals (for ERC-20 tokens).
 *  Returns null if the input is not a valid positive number. */
export function parseAmountWithDecimals(displayAmount: string, decimals: number): string | null {
  const trimmed = displayAmount.trim();
  if (!trimmed || trimmed === '-') return null;

  const parts = trimmed.split('.');
  if (parts.length > 2) return null;

  const wholePart = parts[0] || '0';
  const fracPart = parts[1] || '';

  if (!/^\d+$/.test(wholePart)) return null;
  if (fracPart && !/^\d+$/.test(fracPart)) return null;

  const truncatedFrac = fracPart.slice(0, decimals).padEnd(decimals, '0');
  const raw = BigInt(wholePart) * 10n ** BigInt(decimals) + BigInt(truncatedFrac || '0');
  if (raw <= 0n) return null;

  return raw.toString();
}

/**
 * Convert balance from smallest unit to display unit using explicit decimals
 * (for ERC-20 tokens). Invalid decimals or a non-integer balance return the
 * input unchanged (raw base units) instead of throwing.
 */
export function formatBalanceWithDecimals(balance: string, decimals: number): string {
  if (!isSafeDecimals(decimals)) return balance;
  const raw = parseBaseUnitString(balance);
  if (raw === null) return balance;
  if (decimals === 0) return raw.toString();
  const divisor = 10n ** BigInt(decimals);
  const whole = raw / divisor;
  const remainder = raw % divisor;
  if (remainder === 0n) return whole.toString();
  const fracStr = remainder.toString().padStart(decimals, '0').replace(/0+$/, '');
  return `${whole.toString()}.${fracStr}`;
}

// Chain display helpers
export const chainLabels: Record<string, string> = {
  btc: 'Bitcoin',
  ltc: 'Litecoin',
  doge: 'Dogecoin',
  rvn: 'Ravencoin',
  flux: 'Flux',
  zec: 'Zcash',
  bch: 'Bitcoin Cash',
  eth: 'Ethereum',
  bsc: 'BNB Chain',
  avax: 'Avalanche',
  polygon: 'Polygon',
  base: 'Base',
  btcTestnet: 'BTC Testnet',
  btcSignet: 'BTC Signet',
  sepolia: 'Sepolia Testnet',
  amoy: 'Amoy Testnet',
  fluxTestnet: 'Flux Testnet',
  solDevnet: 'Solana Devnet',
  solMainnet: 'Solana',
};

export const chainSymbols: Record<string, string> = {
  btc: 'BTC',
  ltc: 'LTC',
  doge: 'DOGE',
  rvn: 'RVN',
  flux: 'FLUX',
  zec: 'ZEC',
  bch: 'BCH',
  eth: 'ETH',
  bsc: 'BNB',
  avax: 'AVAX',
  polygon: 'POL',
  base: 'ETH',
  btcTestnet: 'TEST-BTC',
  btcSignet: 'TEST-BTC',
  sepolia: 'TEST-ETH',
  amoy: 'TEST-POL',
  fluxTestnet: 'TEST-FLUX',
  solDevnet: 'TEST-SOL',
  solMainnet: 'SOL',
};

export const chainLogos: Record<string, string> = {
  btc: btcLogo,
  ltc: ltcLogo,
  doge: dogeLogo,
  rvn: rvnLogo,
  flux: fluxLogo,
  zec: zecLogo,
  bch: bchLogo,
  eth: ethLogo,
  bsc: bscLogo,
  avax: avaxLogo,
  polygon: polLogo,
  base: baseLogo,
  btcTestnet: btcTestnetLogo,
  btcSignet: btcSignetLogo,
  sepolia: sepoliaLogo,
  amoy: amoyLogo,
  fluxTestnet: fluxTestnetLogo,
  solDevnet: solDevnetLogo,
  solMainnet: solMainnetLogo,
};

// Explorer URLs — matching ssp-wallet backends (explorer ?? node).
// `suffix` is appended after the txid/address (e.g. `?cluster=devnet` for
// Solana Explorer on devnet). Concat pattern: `${base.address}${addr}${base.suffix ?? ''}`.
export const chainExplorers: Record<string, { address: string; tx: string; suffix?: string }> = {
  btc: {
    address: 'https://blockbook-btc.sspwallet.io/address/',
    tx: 'https://blockbook-btc.sspwallet.io/tx/',
  },
  ltc: {
    address: 'https://blockbook-ltc.sspwallet.io/address/',
    tx: 'https://blockbook-ltc.sspwallet.io/tx/',
  },
  doge: {
    address: 'https://blockbook-doge.sspwallet.io/address/',
    tx: 'https://blockbook-doge.sspwallet.io/tx/',
  },
  rvn: {
    address: 'https://blockbook-rvn.sspwallet.io/address/',
    tx: 'https://blockbook-rvn.sspwallet.io/tx/',
  },
  flux: {
    address: 'https://flux-explorer.sspwallet.io/address/',
    tx: 'https://flux-explorer.sspwallet.io/tx/',
  },
  zec: {
    address: 'https://blockbook-zec.sspwallet.io/address/',
    tx: 'https://blockbook-zec.sspwallet.io/tx/',
  },
  bch: {
    address: 'https://blockbook-bch.sspwallet.io/address/',
    tx: 'https://blockbook-bch.sspwallet.io/tx/',
  },
  eth: {
    address: 'https://etherscan.io/address/',
    tx: 'https://etherscan.io/tx/',
  },
  bsc: {
    address: 'https://bscscan.com/address/',
    tx: 'https://bscscan.com/tx/',
  },
  avax: {
    address: 'https://snowtrace.io/address/',
    tx: 'https://snowtrace.io/tx/',
  },
  polygon: {
    address: 'https://polygonscan.com/address/',
    tx: 'https://polygonscan.com/tx/',
  },
  base: {
    address: 'https://basescan.org/address/',
    tx: 'https://basescan.org/tx/',
  },
  btcTestnet: {
    address: 'https://blockbook-btc-testnet.sspwallet.io/address/',
    tx: 'https://blockbook-btc-testnet.sspwallet.io/tx/',
  },
  btcSignet: {
    address: 'https://blockbook-btc-signet.sspwallet.io/address/',
    tx: 'https://blockbook-btc-signet.sspwallet.io/tx/',
  },
  sepolia: {
    address: 'https://sepolia.etherscan.io/address/',
    tx: 'https://sepolia.etherscan.io/tx/',
  },
  amoy: {
    address: 'https://amoy.polygonscan.com/address/',
    tx: 'https://amoy.polygonscan.com/tx/',
  },
  fluxTestnet: {
    address: 'https://flux-testnet.sspwallet.io/address/',
    tx: 'https://flux-testnet.sspwallet.io/tx/',
  },
  // Solana Explorer defaults to mainnet-beta — devnet entries MUST append
  // ?cluster=devnet via the `suffix` field, otherwise the link opens to a
  // "not found" page on mainnet explorer.
  solDevnet: {
    address: 'https://explorer.solana.com/address/',
    tx: 'https://explorer.solana.com/tx/',
    suffix: '?cluster=devnet',
  },
  // Mainnet needs NO cluster suffix — Solana Explorer defaults to
  // mainnet-beta, and passing one would break the link.
  solMainnet: {
    address: 'https://explorer.solana.com/address/',
    tx: 'https://explorer.solana.com/tx/',
  },
};

/**
 * Build a full explorer URL for an address or txid on the given chain.
 * Appends the per-chain suffix (e.g. `?cluster=devnet` for solDevnet) so
 * callers don't have to remember the Solana Explorer cluster gotcha at
 * every link site. Returns empty string if the chain has no explorer
 * configured (graceful no-link rendering).
 */
export function buildExplorerUrl(chain: string, kind: 'tx' | 'address', value: string): string {
  const exp = chainExplorers[chain];
  if (!exp) return '';
  const base = kind === 'tx' ? exp.tx : exp.address;
  return `${base}${value}${exp.suffix ?? ''}`;
}

export const chainScriptTypes: Record<string, number> = {
  btc: 2,
  ltc: 2,
  doge: 0,
  rvn: 0,
  flux: 0,
  zec: 0,
  bch: 0,
  btcTestnet: 2,
  btcSignet: 2,
  fluxTestnet: 0,
};

export const chainCoinTypes: Record<string, number> = {
  btc: 0,
  ltc: 2,
  doge: 3,
  rvn: 175,
  flux: 19167,
  zec: 133,
  bch: 145,
  eth: 60,
  bsc: 9006,
  avax: 9005,
  polygon: 966,
  base: 8453,
  btcTestnet: 1,
  btcSignet: 1,
  sepolia: 1,
  amoy: 1,
  fluxTestnet: 1,
};

// Basic address format validation per chain (catches typos, wrong-chain addresses).
// The backend performs full cryptographic validation — this is a fast client-side check.
const EVM_ADDRESS_RE = /^0x[a-fA-F0-9]{40}$/;
const BASE58_RE = /^[123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz]+$/;

const chainAddressRules: Record<string, (addr: string) => boolean> = {
  btc: (a) =>
    /^bc1[a-z0-9]{25,90}$/.test(a) || // bech32 / bech32m
    (/^[13]/.test(a) && a.length >= 25 && a.length <= 34 && BASE58_RE.test(a)), // legacy / P2SH
  btcTestnet: (a) =>
    /^tb1[a-z0-9]{25,90}$/.test(a) ||
    (/^[mn2]/.test(a) && a.length >= 25 && a.length <= 34 && BASE58_RE.test(a)),
  btcSignet: (a) =>
    /^tb1[a-z0-9]{25,90}$/.test(a) ||
    (/^[mn2]/.test(a) && a.length >= 25 && a.length <= 34 && BASE58_RE.test(a)),
  ltc: (a) =>
    /^ltc1[a-z0-9]{25,90}$/.test(a) ||
    (/^[LM3]/.test(a) && a.length >= 25 && a.length <= 34 && BASE58_RE.test(a)),
  doge: (a) => /^[DA9]/.test(a) && a.length >= 25 && a.length <= 34 && BASE58_RE.test(a),
  flux: (a) => /^t[13]/.test(a) && a.length >= 25 && a.length <= 35 && BASE58_RE.test(a),
  fluxTestnet: (a) => /^t/.test(a) && a.length >= 25 && a.length <= 35 && BASE58_RE.test(a),
  zec: (a) => /^t[13]/.test(a) && a.length >= 25 && a.length <= 35 && BASE58_RE.test(a),
  bch: (a) =>
    /^bitcoincash:/.test(a) || // cashaddr
    (/^[13]/.test(a) && a.length >= 25 && a.length <= 34 && BASE58_RE.test(a)), // legacy
  rvn: (a) => /^[Rr]/.test(a) && a.length >= 25 && a.length <= 34 && BASE58_RE.test(a),
  // Solana ed25519 pubkeys are base58, typically 32-44 chars (decodes to 32 bytes).
  solDevnet: (a) => a.length >= 32 && a.length <= 44 && BASE58_RE.test(a),
  solMainnet: (a) => a.length >= 32 && a.length <= 44 && BASE58_RE.test(a),
};

// ============================================================
// Contacts
// ============================================================

export interface Contact {
  id: string;
  organizationId: string;
  chain: string;
  name: string;
  address: string;
  notes?: string;
  createdBy: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt?: string;
  usageCount: number;
  lastUsedAt?: string;
  lastUsedBy?: string;
}

export interface CreateContactRequest {
  chain: string;
  name: string;
  address: string;
  notes?: string;
}

export interface UpdateContactRequest {
  name?: string;
  address?: string;
  notes?: string;
}

export interface ContactResponse {
  success: boolean;
  contact?: Contact;
  error?: string;
  errorCode?: string;
}

export interface ContactsListResponse {
  success: boolean;
  contacts?: Contact[];
  total?: number;
  error?: string;
  errorCode?: string;
}

// ============================================================
// Spending Policies
// ============================================================

export type WhitelistMode = 'disabled' | 'warn' | 'enforce';

// ── Advanced Policy Engine P5 — destination filtering & IP/geo controls ──

/**
 * Global destination control (COORDINATION-LAYER only — gates the SSP-mediated
 * proposal flow, never the keys; a quorum with external tooling can always
 * transact). Runs as an unbypassable pre-check (an allow rule cannot skip it):
 * - 'any'                    → no destination restriction (default)
 * - 'internal_only'          → recipients must be an address of ANOTHER vault
 *                              in the SAME org (an internal transfer)
 * - 'whitelisted_only'       → recipients must be whitelisted (alias of
 *                              whitelist.mode='enforce', run as a pre-check)
 * - 'internal_and_whitelisted' → recipients must be internal OR whitelisted
 */
export type DestinationType =
  | 'any'
  | 'internal_only'
  | 'whitelisted_only'
  | 'internal_and_whitelisted';

export const DESTINATION_TYPES: DestinationType[] = [
  'any',
  'internal_only',
  'whitelisted_only',
  'internal_and_whitelisted',
];

/**
 * IP / geo restrictions on the RELAY-mediated proposal-create and -sign path.
 * COORDINATION-LAYER control only — it gates who can drive the SSP flow and from
 * where; it does NOT and CANNOT prevent a quorum from signing with external
 * tooling. Never describe this as "preventing signing".
 */
export interface IpRestrictionsConfig {
  enabled: boolean;
  mode: 'allowlist' | 'blocklist';
  /** IPv4/IPv6 CIDR ranges (or bare IPs), e.g. '203.0.113.0/24', '2001:db8::/32'. */
  cidrs: string[];
  /** ISO-3166 alpha-2 country codes to block (e.g. 'KP', 'IR'), uppercased server-side. */
  geoBlock: string[];
  onViolation: 'block' | 'require_approval';
}

/** Server-enforced caps (mirrored client-side for inline validation). */
export const MAX_IP_CIDRS = 100;
export const MAX_GEO_COUNTRIES = 250;

export interface PolicyLimit {
  coin?: string; // Amount in smallest unit (satoshi/wei)
  usd?: number; // USD equivalent
}

export interface TokenLimit {
  symbol: string;
  contractAddress?: string; // ERC-20 contract (undefined = native coin)
  decimals?: number; // Token decimals for display conversion
  // Coin-denominated limits (in smallest unit — e.g., wei, satoshi)
  perTransaction?: string;
  daily?: string;
  weekly?: string;
  monthly?: string;
  // USD-denominated limits (fiat equivalent at evaluation time)
  perTransactionUsd?: number;
  dailyUsd?: number;
  weeklyUsd?: number;
  monthlyUsd?: number;
}

export interface WhitelistedAddress {
  address: string;
  label?: string;
  chain?: string;
  addedBy: string;
  addedAt: string;
  /**
   * Whitelist cool-down (Advanced Policy Engine P3): ISO instant at which the
   * entry becomes ACTIVE. Server-stamped at add-time when the org's
   * whitelistCooldownHours > 0. While cooling, transfers to the address raise
   * a 'whitelist_cooldown' violation. Absent = active immediately.
   */
  cooldownUntil?: string;
}

// ============================================================
// Advanced Policy Engine — Phase 3 (Time & Frequency Controls)
// Optional, absent-by-default sections on VaultPolicy. Shapes mirror
// ssp-relay-enterprise/src/types/vault.ts (dates as ISO strings).
// ============================================================

export type ScheduleDay = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

/**
 * One schedule window. Times are 'HH:mm' (24h) local to the schedule's IANA
 * timezone. Overnight windows (startTime > endTime) span midnight into the
 * following day. startTime === endTime is invalid.
 */
export interface ScheduleWindow {
  days: ScheduleDay[];
  startTime: string;
  endTime: string;
}

/**
 * Schedule/business-hours policy (vault- and org-level PRE-CHECK — evaluated
 * before ordered rules, so no rule can bypass it). Violation type:
 * 'schedule_block'.
 */
export interface ScheduleConfig {
  /** IANA timezone name. */
  timezone: string;
  /** 1–20 windows. */
  windows: ScheduleWindow[];
  mode: 'allow_during' | 'block_during';
  onViolation: 'block' | 'require_approval';
}

/**
 * Configurable proposal expiry (vault- and org-level; vault wins). Absent or
 * enabled=false → the fixed 7-day default. Snapshot at proposal creation.
 */
export interface ProposalExpiryConfig {
  enabled: boolean;
  /** 1..720 hours (30 days max). Default 72 when enabling. */
  expiryHours: number;
  /** Optional pre-expiry warning lead time in minutes (5..43200). */
  warnBeforeMinutes?: number;
}

/** Per-signer transaction-count windows within txCountLimits. */
export interface SignerTxCountLimit {
  wkIdentity: string;
  daily?: number;
  weekly?: number;
  monthly?: number;
}

/**
 * Count-based transaction limits (rolling 24h/7d/30d windows). Gated by the
 * velocityLimits entitlement, like USD window limits. Violation type:
 * 'tx_count_limit'.
 */
export interface TxCountLimitsConfig {
  daily?: number;
  weekly?: number;
  monthly?: number;
  perSigner?: SignerTxCountLimit[];
}

export interface SignerPolicy {
  wkIdentity: string;
  label?: string;
  aggregateLimits?: {
    perTransaction?: { usd: number };
    daily?: { usd: number };
    weekly?: { usd: number };
    monthly?: { usd: number };
  };
  tokenLimits?: TokenLimit[];
}

export interface VaultPolicy {
  id: string;
  organizationId: string;
  vaultId?: string; // null = org default policy

  // Aggregate limits (USD across all assets)
  aggregateLimits: {
    perTransaction?: { usd: number };
    daily?: { usd: number };
    weekly?: { usd: number };
    monthly?: { usd: number };
  };

  // Per-token limits (optional overrides)
  tokenLimits: TokenLimit[];

  // Address whitelist
  whitelist: {
    mode: WhitelistMode;
    addresses: WhitelistedAddress[];
  };

  // Time lock
  timeLock: {
    enabled: boolean;
    delayMinutes: number;
    thresholdUsd?: number;
  };

  // Approval overrides
  //
  // NOTE: `requiredSignersOverride` was formally removed (founder decision) —
  // it was never enforced because signature thresholds are structural per
  // chain family. The backend strips it on write, so legacy stored values
  // age out naturally.
  approvalOverrides: {
    highValueThresholdUsd?: number;
    requireAdminApproval: boolean;
  };

  // Per-signer spending limits
  signerPolicies?: SignerPolicy[];

  // Per-chain policy overrides (org-level only)
  chainPolicies?: Record<string, ChainPolicyOverride>;

  // ── Advanced Policy Engine P3 (all optional, absent by default) ──

  // Schedule/business-hours pre-check (vault- and org-level; both apply)
  schedule?: ScheduleConfig;

  // Configurable proposal expiry (vault- and org-level; vault wins)
  proposalExpiry?: ProposalExpiryConfig;

  // Count-based transaction limits (vault- and org-level; vault wins)
  txCountLimits?: TxCountLimitsConfig;

  /**
   * Whitelist cool-down in hours — ORG-LEVEL SETTING ONLY (stored on the org
   * default policy). 0 or absent = disabled. When > 0, new whitelist entries
   * anywhere in the org are stamped with cooldownUntil = addedAt + hours.
   */
  whitelistCooldownHours?: number;

  /**
   * P4 governance config — ORG-LEVEL ONLY (present on the org default policy).
   * Absent/disabled = mutations apply directly.
   */
  policyGovernance?: PolicyGovernanceConfig;

  // ── Advanced Policy Engine P5 (all optional, absent by default) ──

  /** Global destination pre-check (vault- and org-level). Absent = 'any'. */
  destinationType?: DestinationType;

  /** IP/geo restrictions on the relay-mediated flow. Absent/disabled = no gating. */
  ipRestrictions?: IpRestrictionsConfig;

  updatedBy: string;
  updatedAt: string;
  createdAt: string;
}

export interface ChainPolicyOverride {
  aggregateLimits?: {
    perTransaction?: { usd: number };
    daily?: { usd: number };
    weekly?: { usd: number };
    monthly?: { usd: number };
  };
  tokenLimits?: TokenLimit[];
  whitelist?: {
    mode: WhitelistMode;
    addresses: WhitelistedAddress[];
  };
  timeLock?: {
    enabled: boolean;
    delayMinutes: number;
    thresholdUsd?: number;
  };
  approvalOverrides?: {
    highValueThresholdUsd?: number;
    requireAdminApproval: boolean;
  };
}

export interface PolicyViolation {
  type:
    | 'per_tx_limit'
    | 'daily_limit'
    | 'weekly_limit'
    | 'monthly_limit'
    | 'per_tx_token_limit'
    | 'daily_token_limit'
    | 'weekly_token_limit'
    | 'monthly_token_limit'
    | 'whitelist'
    | 'high_value'
    | 'rule_block'
    // P3 — schedule pre-check violation (outside an allow_during window /
    // inside a block_during window)
    | 'schedule_block'
    // P3 — recipient matches only whitelist entries whose cool-down is still
    // active (limit carries the ISO activation time)
    | 'whitelist_cooldown'
    // P3 — rolling-window transaction count limit
    | 'tx_count_limit'
    // P6 — compliance/AML screening flagged or blocked the destination, or the
    // provider was unavailable under a fail-closed configuration. Tighten-only:
    // compliance can escalate/block but never loosen a rule verdict.
    | 'compliance_risk'
    // EVM calldata the policy engine can't price as a plain transfer (unknown
    // or batched contract call) — requires admin approval (or is blocked).
    | 'contract_call';
  message: string;
  limit?: string;
  actual?: string;
}

// ============================================================
// Policy Rules (ordered rules engine — first-match-wins)
// ============================================================

export type PolicyConditionOp = 'gt' | 'gte' | 'lt' | 'lte' | 'eq';
export type PolicyRulePeriod = 'daily' | 'weekly' | 'monthly';

/**
 * A single condition inside a rule. All conditions in a rule are ANDed
 * together (1-10 per rule).
 *
 * NOTE: `amount_usd` NEVER matches token transfers (tokenContract present
 * ⇒ false) — use `amount_coin` for tokens.
 */
export type PolicyCondition =
  | { type: 'amount_usd'; op: PolicyConditionOp; valueUsd: number }
  | {
      type: 'amount_coin';
      op: PolicyConditionOp;
      value: string; // base-unit decimal digits, > 0
      tokenContract?: string; // absent = native only; present = that token only
    }
  | { type: 'initiator'; wkIdentities: string[] }
  | { type: 'asset'; native?: boolean; tokenContracts?: string[] }
  | { type: 'chain'; chains: string[] }
  | { type: 'destination'; match: 'whitelisted' }
  | { type: 'destination'; match: 'specific'; addresses: string[] }
  | { type: 'velocity_usd'; period: PolicyRulePeriod; op: 'gt'; valueUsd: number }
  | { type: 'tx_count'; period: PolicyRulePeriod; op: 'gt'; value: number }
  // Phase 4 — proposal USD amount as a percentage of the vault's total USD
  // balance (0.1..100). Fail-closed 'balance_unavailable' when the balance is
  // stale/missing. Like amount_usd, NEVER matches token transfers.
  | { type: 'amount_pct_balance'; op: 'gt' | 'gte'; percent: number }
  // Phase 6 — synchronous outbound webhook decision. During proposal creation
  // the engine POSTs a signed (SSP-Signature HMAC) payload to `url` and reads a
  // { decision: 'allow'|'block'|'require_approval' } response body. This is a
  // COORDINATION-LAYER control on the SSP-mediated proposal flow — it does NOT
  // and cannot prevent a quorum from signing with external tooling.
  //
  // The secret is WRITE-ONLY: `secret` (plaintext) is sent on create/update and
  // is NEVER returned; the server strips it and instead signals `secretSet`.
  // Requires the org's `integrations` entitlement to actually fire outbound.
  | {
      type: 'webhook';
      url: string;
      /** Per-call budget, clamped 1000..10000ms server-side (default 5000). */
      timeoutMs?: number;
      /** Verdict applied on timeout / error / unreachable endpoint. */
      onFailure: 'block' | 'allow';
      /** WRITE-ONLY plaintext HMAC secret. Omit on update to keep the stored one. */
      secret?: string;
      /** READ-ONLY: true when a secret is stored (the secret itself is never returned). */
      secretSet?: boolean;
    };

export type PolicyConditionType = PolicyCondition['type'];

// ============================================================
// Approval Groups & Multi-Round Workflows (Phase 2)
// ============================================================

/**
 * Named, reusable approver group (org-scoped, unique name per org).
 * Members are wkIdentity strings validated against org memberships at
 * write time; groups can dangle when members later leave (unsatisfiable
 * is a WARN flag, never a block).
 */
export interface ApprovalGroup {
  id: string;
  organizationId: string;
  name: string;
  description?: string;
  members: string[];
  requiredApprovals: number;
  /** Members that currently resolve to an active org membership. */
  resolvableMemberCount: number;
  /** WARN only: requiredApprovals exceeds resolvableMemberCount. */
  unsatisfiable: boolean;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
}

export interface CreateApprovalGroupRequest {
  name: string;
  description?: string;
  members: string[];
  requiredApprovals: number;
}

export interface UpdateApprovalGroupRequest {
  name?: string;
  description?: string;
  members?: string[];
  requiredApprovals?: number;
}

export interface ApprovalGroupsListResponse {
  success: boolean;
  groups?: ApprovalGroup[];
  error?: string;
  errorCode?: string;
}

export interface ApprovalGroupEnvelope {
  success: boolean;
  group?: ApprovalGroup;
  /** On GROUP_IN_USE delete failures: names of the rules referencing the group. */
  referencingRules?: string[];
  error?: string;
  errorCode?: string;
}

/**
 * What happens when the current round passes its timeoutAt without quorum:
 * reject the proposal, escalate (skip) to the next round, or auto-approve
 * the round. Escalate on the FINAL round degrades to reject.
 */
export type WorkflowTimeoutAction = 'reject' | 'escalate' | 'auto_approve';

/**
 * One round of a sequential approval workflow — approvers come from EITHER
 * a named approval group OR an inline approvers list (exactly one).
 */
export interface ApprovalWorkflowRoundConfig {
  name: string;
  approverGroupId?: string;
  approvers?: string[];
  /** Approvals needed to complete the round (>= 1). */
  required: number;
  /** Optional round deadline; absent = the round never times out. */
  timeoutMinutes?: number;
}

/**
 * Optional multi-round workflow attached to require_approval /
 * require_approval_and_timelock actions. Rules WITHOUT workflow config keep
 * the plain single-admin adminApproval path unchanged.
 */
export interface ApprovalWorkflowConfig {
  /** 1–5 sequential rounds, evaluated in order. */
  rounds: ApprovalWorkflowRoundConfig[];
  onTimeout: WorkflowTimeoutAction;
}

/** One round of a proposal's approval-workflow state (server-serialized). */
export interface ProposalWorkflowRound {
  roundIndex: number;
  name: string;
  required: number;
  /** Resolved at proposal creation — a snapshot, later group edits don't apply. */
  approverWkIdentities: string[];
  approvals: Array<{ wkIdentity: string; approvedAt: string }>;
  rejections: Array<{ wkIdentity: string; rejectedAt: string; reason?: string }>;
  status: 'pending' | 'approved' | 'rejected' | 'timed_out';
  startedAt?: string;
  timeoutAt?: string;
  approvedByTimeout?: boolean;
  escalated?: boolean;
}

/**
 * Proposal approval-workflow state — present only on workflow-bearing
 * proposals, mutually exclusive with adminApproval. Proposals without it
 * traverse the single-admin path exactly as before.
 */
export interface ProposalApprovalWorkflow {
  currentRound: number;
  status: 'in_progress' | 'fully_approved' | 'rejected' | 'timed_out';
  onTimeout: WorkflowTimeoutAction;
  rounds: ProposalWorkflowRound[];
}

/**
 * The verdict a matched rule produces. `allow` bypasses the vault's flat
 * policy limits entirely (freeze and pre-checks still apply) — surfaced
 * with a permanent warning banner in the UI.
 */
export type PolicyRuleAction =
  | { type: 'allow' }
  | { type: 'block'; reason?: string }
  | { type: 'require_approval'; workflow?: ApprovalWorkflowConfig }
  | { type: 'timelock'; delayMinutes: number }
  | {
      type: 'require_approval_and_timelock';
      delayMinutes: number;
      workflow?: ApprovalWorkflowConfig;
    };

export type PolicyRuleActionType = PolicyRuleAction['type'];

export interface PolicyRule {
  id: string;
  organizationId: string;
  /** Absent for ORG-level rules (they apply to every vault). */
  vaultId?: string;
  name: string;
  description?: string;
  enabled: boolean;
  priority: number; // 10, 20, 30… ascending = evaluation order
  conditions: PolicyCondition[];
  action: PolicyRuleAction;
  /**
   * P5 — set when the rule was materialized by applying a policy template.
   * Surfaces a subtle "from template" origin tag; the rule stays fully editable.
   */
  templateId?: string;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
}

export interface RuleEvaluationTrace {
  ruleId: string;
  ruleName: string;
  matched: boolean;
  action?: string; // only present on the matched entry
}

export interface CreatePolicyRuleRequest {
  name: string;
  description?: string;
  enabled?: boolean; // default true
  conditions: PolicyCondition[];
  action: PolicyRuleAction;
}

export interface UpdatePolicyRuleRequest {
  name?: string;
  description?: string; // empty string clears
  enabled?: boolean;
  conditions?: PolicyCondition[];
  action?: PolicyRuleAction;
}

export interface PolicyRulesListResponse {
  success: boolean;
  rules?: PolicyRule[];
  error?: string;
  errorCode?: string;
  // reorder is governance-interceptable — carries a change request instead.
  governanceRequired?: boolean;
  changeRequest?: PolicyChangeRequest;
}

export interface PolicyRuleEnvelope {
  success: boolean;
  rule?: PolicyRule;
  error?: string;
  errorCode?: string;
  // When org governance is enabled, a mutation is captured as a change
  // request INSTEAD of applying — the response carries these instead of `rule`.
  governanceRequired?: boolean;
  changeRequest?: PolicyChangeRequest;
}

// ============================================================
// Compliance / AML & Webhook Policy Decisions (Phase 6)
//
// SECURITY MODEL: webhook + compliance decisions are COORDINATION-LAYER controls
// on the SSP-mediated proposal flow — NOT custody enforcement. A quorum with
// external tooling can always transact. Copy must never claim these prevent
// signing. All outbound calls are gated by the org's `integrations` entitlement,
// an org kill-switch (`outboundDisabled`), and per-org rate limiting.
// ============================================================

/**
 * Screening provider. Only `generic_webhook` is implemented — the org points it
 * at their own screening endpoint. The named vendors are shown GREYED /
 * contact-us in the UI and are rejected by the service until contracts exist.
 */
export type ComplianceProvider = 'generic_webhook' | 'chainalysis' | 'elliptic' | 'trm' | 'crystal';

/** Provider values the backend actually accepts today. */
export const IMPLEMENTED_COMPLIANCE_PROVIDERS: ComplianceProvider[] = ['generic_webhook'];

/** Named vendors shown greyed / coming-soon in the UI (no adapter yet). */
export const GREYED_COMPLIANCE_PROVIDERS: ComplianceProvider[] = [
  'chainalysis',
  'elliptic',
  'trm',
  'crystal',
];

/** What to do when a screened address comes back high-risk. */
export type ComplianceOnHighRisk = 'block' | 'require_approval';

/**
 * What to do when the provider is unreachable / times out / errors. Default is
 * `block` (fail-closed). `allow_with_warning` lets the proposal proceed but
 * annotates it with an `unavailable` compliance result.
 */
export type ComplianceOnProviderUnavailable = 'block' | 'allow_with_warning';

/**
 * Org compliance configuration as returned by the API. Secrets are STRIPPED:
 * `apiKeyRef` is present only to signal a secret is stored — it never contains
 * the secret itself.
 */
export interface ComplianceConfigResponse {
  organizationId: string;
  enabled: boolean;
  provider: ComplianceProvider;
  endpointUrl?: string;
  /** Present ONLY as a "a secret is set" signal — never the secret value. */
  apiKeyRef?: string;
  /** 0..100; addresses at/above this score are treated as high-risk. */
  riskThreshold?: number;
  onHighRisk: ComplianceOnHighRisk;
  onProviderUnavailable: ComplianceOnProviderUnavailable;
  timeoutMs?: number;
  /** Kill-switch: when true, no outbound compliance calls fire. */
  outboundDisabled?: boolean;
  updatedBy: string;
  updatedAt: string;
}

/**
 * Full-config write body. `secret` is WRITE-ONLY plaintext — omit to keep the
 * stored secret. Greyed vendors are rejected by the service.
 */
export interface SetComplianceConfigRequest {
  enabled: boolean;
  provider: ComplianceProvider;
  endpointUrl?: string;
  /** WRITE-ONLY plaintext secret; omit to keep the existing one. */
  secret?: string;
  riskThreshold?: number;
  onHighRisk?: ComplianceOnHighRisk;
  onProviderUnavailable?: ComplianceOnProviderUnavailable;
  timeoutMs?: number;
}

/** Kill-switch-only PATCH body (flips outbound without touching full config). */
export interface ComplianceKillSwitchRequest {
  outboundDisabled: boolean;
}

export interface ComplianceConfigEnvelope {
  success: boolean;
  config?: ComplianceConfigResponse | null;
  error?: string;
  errorCode?: string;
}

/** Verdict of a single address screen. */
export type ComplianceScreenStatus = 'clean' | 'flagged' | 'blocked' | 'unavailable';

export interface ComplianceScreenResult {
  status: ComplianceScreenStatus;
  riskScore?: number;
  flags?: string[];
  reason?: string;
}

export interface ComplianceScreenRequest {
  chain: string;
  address: string;
}

export interface ComplianceScreenEnvelope {
  success: boolean;
  result?: ComplianceScreenResult;
  error?: string;
  errorCode?: string;
}

/** Compliance result embedded on a proposal's policyCheck. */
export interface ProposalComplianceCheck {
  provider: ComplianceProvider;
  status: ComplianceScreenStatus;
  riskScore?: number;
  flags?: string[];
  checkedAt: string;
}

/**
 * Stateless webhook connectivity test. Fires exactly ONE signed request; no
 * persistence, no proposal effect, no decision-log write. Never throws — a
 * timeout/error is returned as { ok: false, error }.
 */
export interface PolicyWebhookTestRequest {
  url: string;
  /** WRITE-ONLY secret used only for this test's HMAC signature. */
  secret?: string;
  /** Clamped 1000..10000ms server-side (default 5000). */
  timeoutMs?: number;
}

export interface PolicyWebhookTestResult {
  ok: boolean;
  decision?: 'allow' | 'block' | 'require_approval';
  httpStatus?: number;
  latencyMs: number;
  /** HOST-ONLY redacted target (never the full URL / query / secret). */
  redactedTarget: string;
  error?: string;
}

export interface PolicyWebhookTestEnvelope {
  success: boolean;
  result?: PolicyWebhookTestResult;
  error?: string;
  errorCode?: string;
}

/**
 * One row of the decision log. Redacted: `redactedTarget` is HOST-ONLY. Newest
 * first. `kind` distinguishes webhook rule decisions from compliance screens.
 */
export type PolicyDecisionKind = 'webhook' | 'compliance';

export type PolicyDecisionOutcome =
  | 'allow'
  | 'block'
  | 'require_approval'
  | 'clean'
  | 'flagged'
  | 'blocked'
  | 'unavailable'
  | 'rate_limited'
  | 'entitlement_disabled'
  | 'kill_switch'
  | 'skipped_preview';

export interface PolicyDecisionLogEntry {
  id: string;
  organizationId: string;
  vaultId?: string;
  kind: PolicyDecisionKind;
  outcome: PolicyDecisionOutcome;
  latencyMs: number;
  ruleId?: string;
  provider?: ComplianceProvider;
  /** HOST-ONLY redacted target. */
  redactedTarget?: string;
  detail?: string;
  createdAt: string;
}

export interface PolicyDecisionLogsQuery {
  vaultId?: string;
  kind?: PolicyDecisionKind;
  limit?: number;
}

export interface PolicyDecisionLogsResponse {
  success: boolean;
  logs?: PolicyDecisionLogEntry[];
  error?: string;
  errorCode?: string;
}

// ============================================================
// Policy Change Governance (Phase 4)
//
// When an org enables policyGovernance, every policy/rule/whitelist mutation
// is intercepted and captured as a PolicyChangeRequest requiring N approvals
// from members holding the configured approverRole — instead of applying.
// ============================================================

export type PolicyGovernanceApproverRole = 'admin' | 'owner';

/**
 * N-of-M governance over policy mutations. ORG-LEVEL SETTING ONLY (stored on
 * the org default policy). When enabled, disabling it ALSO requires quorum —
 * that is what protects the config against a hijacked admin. Coordination-layer
 * control only — never custody-level enforcement.
 */
export interface PolicyGovernanceConfig {
  enabled: boolean;
  approverRole: PolicyGovernanceApproverRole;
  /** Distinct approvals needed to apply a change (1..100). */
  requiredApprovals: number;
  /** Optional delay (hours, 0..8760) between reaching quorum and applying. */
  cooldownHours?: number;
}

export type PolicyChangeType =
  | 'vault_policy_update'
  | 'vault_whitelist_mode'
  | 'vault_whitelist_add'
  | 'vault_whitelist_remove'
  | 'vault_rule_create'
  | 'vault_rule_update'
  | 'vault_rule_delete'
  | 'vault_rules_reorder'
  | 'org_policy_update'
  | 'org_chain_policy_update'
  | 'org_chain_policy_delete'
  | 'org_rule_create'
  | 'org_rule_update'
  | 'org_rule_delete'
  | 'org_rules_reorder';

export type PolicyChangeStatus = 'pending' | 'approved' | 'applied' | 'rejected' | 'expired';

export interface PolicyChangeApproval {
  wkIdentity: string;
  at: string;
}

export interface PolicyChangeRequest {
  id: string;
  organizationId: string;
  /** Present for vault-scoped change types; absent for org-scoped ones. */
  vaultId?: string;
  changeType: PolicyChangeType;
  /** Human-readable one-line summary for the approval UI. */
  changeSummary: string;
  changePayload: Record<string, unknown>;
  approverRole: PolicyGovernanceApproverRole;
  requiredApprovals: number;
  cooldownHours?: number;
  requestedBy: string;
  status: PolicyChangeStatus;
  approvals: PolicyChangeApproval[];
  createdAt: string;
  expiresAt: string;
  approvedAt?: string;
  applyAfter?: string;
  appliedAt?: string;
  rejectedAt?: string;
  rejectedBy?: string;
  error?: string;
  applyFailedAt?: string;
}

/** Backend list shape uses `changeRequests` (plural) — code wins over the doc. */
export interface PolicyChangesListResponse {
  success: boolean;
  changeRequests?: PolicyChangeRequest[];
  error?: string;
  errorCode?: string;
}

export interface PolicyChangeEnvelope {
  success: boolean;
  changeRequest?: PolicyChangeRequest;
  error?: string;
  errorCode?: string;
}

/**
 * Discriminated outcome of a governance-interceptable policy mutation: the
 * change either applied directly (returning the fresh entity) or was captured
 * as a pending change request. Callers MUST branch on `governanceRequired` so
 * the UI never shows a false "Saved" when a change is only pending approval.
 */
export type PolicyWriteResult<T> =
  | { governanceRequired: false; value: T }
  | { governanceRequired: true; changeRequest: PolicyChangeRequest };

// ============================================================
// Policy Templates (Phase 5)
//
// Six code-resident presets. Applying a template MATERIALIZES real, editable
// ordered rules AND sets a small set of explicitly-listed flat policy fields
// through the same gated update paths. ipRestrictions are NOT template-settable
// (org-specific). Coordination-layer controls only — never custody enforcement.
// ============================================================

/** The ONLY flat policy fields a template may set (omitted keys stay unchanged). */
export interface PolicyTemplateFlatFields {
  whitelistMode?: WhitelistMode;
  destinationType?: DestinationType;
  timeLock?: { enabled: boolean; delayMinutes: number; thresholdUsd?: number };
  approvalOverrides?: {
    highValueThresholdUsd?: number;
    requireAdminApproval: boolean;
  };
}

export interface PolicyTemplateSummary {
  id: string;
  name: string;
  description: string;
  /** Real CreatePolicyRuleRequest objects — materialized as editable rules on apply. */
  rules: CreatePolicyRuleRequest[];
  flatFields?: PolicyTemplateFlatFields;
}

export interface PolicyTemplatesListResponse {
  success: boolean;
  templates?: PolicyTemplateSummary[];
  error?: string;
  errorCode?: string;
}

/** Preview / apply-template wire response (single endpoint, branched by fields). */
export interface ApplyTemplateResponse {
  success: boolean;
  // preview:true branch
  template?: PolicyTemplateSummary;
  // apply + governance-intercepted branch
  governanceRequired?: boolean;
  changeRequest?: PolicyChangeRequest;
  // apply + direct branch
  templateId?: string;
  appliedRules?: PolicyRule[];
  flatFieldsApplied?: string[];
  error?: string;
  errorCode?: string;
}

/** Discriminated outcome of applying a template (client-side model). */
export type ApplyTemplateResult =
  | { governanceRequired: true; changeRequest: PolicyChangeRequest }
  | {
      governanceRequired: false;
      templateId: string;
      appliedRules: PolicyRule[];
      flatFieldsApplied: string[];
    };

export interface VelocityUsage {
  daily: { used: number; limit: number | null };
  weekly: { used: number; limit: number | null };
  monthly: { used: number; limit: number | null };
}

export interface UpdatePolicyRequest {
  aggregateLimits?: VaultPolicy['aggregateLimits'];
  tokenLimits?: TokenLimit[];
  timeLock?: VaultPolicy['timeLock'];
  approvalOverrides?: VaultPolicy['approvalOverrides'];
  signerPolicies?: SignerPolicy[];
  whitelist?: VaultPolicy['whitelist'];
  // P3 sections — undefined = leave unchanged, null = clear on the server
  schedule?: ScheduleConfig | null;
  proposalExpiry?: ProposalExpiryConfig | null;
  txCountLimits?: TxCountLimitsConfig | null;
  /** Org-level default policy only (0 = disabled). */
  whitelistCooldownHours?: number;
  /** Org-level default policy only — null clears governance. */
  policyGovernance?: PolicyGovernanceConfig | null;
  // P5 additive fields — undefined = leave unchanged, null = clear on the server
  destinationType?: DestinationType | null;
  ipRestrictions?: IpRestrictionsConfig | null;
}

export interface UpdateWhitelistModeRequest {
  mode: WhitelistMode;
}

export interface AddWhitelistAddressRequest {
  address: string;
  label?: string;
  chain?: string;
}

export interface PolicyPreviewRequest {
  recipients: ProposalRecipient[];
  chain?: string;
  tokenContract?: string;
  tokenSymbol?: string;
  tokenDecimals?: number;
  // Dry-run unsaved rules IN PLACE OF stored rules (never persisted).
  // Trace ids are synthetic 'inline-<index>' based on submitted position.
  inlineRules?: CreatePolicyRuleRequest[];
}

export interface PolicyEngineEvaluation {
  status: 'compliant' | 'requires_approval' | 'blocked';
  violations: PolicyViolation[];
  amountUsd: number;
  timeLockRequired: boolean;
  timeLockDelayMinutes: number;
  // ABSENT when the vault has zero enabled rules (byte-identical flat result)
  evaluatedRules?: RuleEvaluationTrace[];
  matchedRuleId?: string;
}

export interface PolicyPreviewResponse {
  success: boolean;
  evaluation?: PolicyEngineEvaluation;
  error?: string;
}

export interface EffectiveLimitsResponse {
  success: boolean;
  signerLimits?: {
    perTransaction?: { usd: number };
    daily?: { usd: number };
    weekly?: { usd: number };
    monthly?: { usd: number };
  };
  vaultLimits?: {
    perTransaction?: { usd: number };
    daily?: { usd: number };
    weekly?: { usd: number };
    monthly?: { usd: number };
  };
  effectiveLimits?: {
    perTransaction?: { usd: number };
    daily?: { usd: number };
    weekly?: { usd: number };
    monthly?: { usd: number };
  };
  signerVelocity?: VelocityUsage;
  vaultVelocity?: VelocityUsage;
  error?: string;
}

export interface PolicyResponse {
  success: boolean;
  policy?: VaultPolicy;
  error?: string;
  errorCode?: string;
  // When org governance is enabled, a policy/whitelist mutation is captured as
  // a change request INSTEAD of applying — carries these instead of `policy`.
  governanceRequired?: boolean;
  changeRequest?: PolicyChangeRequest;
}

export interface VelocityResponse {
  success: boolean;
  velocity?: VelocityUsage;
  error?: string;
}

export function isValidAddress(address: string, chain: string): boolean {
  const trimmed = address.trim();
  if (!trimmed) return false;

  if (isEvmChain(chain)) {
    return EVM_ADDRESS_RE.test(trimmed);
  }

  const rule = chainAddressRules[chain];
  if (rule) return rule(trimmed);

  // Unknown chain — accept anything non-empty (backend will validate)
  return trimmed.length >= 10;
}

// ============================================================
// Flux Node Types
// ============================================================

export type FluxNodeTier = 'cumulus' | 'nimbus' | 'stratus';
export type FluxNodeStatus = 'offline' | 'starting' | 'started' | 'confirmed' | 'dos';

export const FLUX_TIER_AMOUNTS: Record<FluxNodeTier, number> = {
  stratus: 4000000000000,
  nimbus: 1250000000000,
  cumulus: 100000000000,
};

export const FLUX_TIER_LABELS: Record<FluxNodeTier, string> = {
  stratus: 'Stratus',
  nimbus: 'Nimbus',
  cumulus: 'Cumulus',
};

// Scope at which a node's Flux daemon identity key is shared.
// See ssp-relay-enterprise/src/types/vault.ts for the authoritative docs.
export type FluxNodeIdentityMode = 'vault' | 'address' | 'node';

export const FLUX_NODE_IDENTITY_MODE_LABELS: Record<FluxNodeIdentityMode, string> = {
  vault: 'Per-vault',
  address: 'Per-address',
  node: 'Per-node',
};

export interface FluxNode {
  id: string;
  organizationId: string;
  vaultId: string;
  txid: string;
  vout: number;
  amount: string;
  addressIndex: number;
  name: string;
  ip: string;
  tier: FluxNodeTier;
  chain: string;
  status: FluxNodeStatus;
  identityPrivKey: string;
  identityPubKey: string;
  identityMode: FluxNodeIdentityMode;
  delegates: string[];
  broadcastTxid?: string;
  lastStartedAt?: string;
  lastConfirmedAt?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface FluxNodeSummary {
  total: number;
  byTier: Record<FluxNodeTier, number>;
  byStatus: Record<FluxNodeStatus, number>;
}

export interface VaultUtxo {
  txid: string;
  vout: number;
  amount: string;
  address: string;
  addressIndex: number;
  confirmations: number;
  locked: boolean;
}

export interface RegisterFluxNodeRequest {
  vaultId: string;
  txid: string;
  vout: number;
  amount: string;
  addressIndex: number;
  name: string;
  ip: string;
  chain: string;
  identityMode?: FluxNodeIdentityMode;
}

export interface UpdateFluxNodeRequest {
  name?: string;
  ip?: string;
  delegates?: string[];
}

export interface FluxNodeStartParams {
  identityPubKey: string;
  collateralTxid: string;
  collateralVout: number;
  collateralAmount: string;
  collateralAddress: string;
  addressIndex: number;
  chain: string;
  delegates: string[];
  orgIndex: number;
  vaultIndex: number;
  redeemScript: string;
  nodeName: string;
  nodeIp: string;
  tier: FluxNodeTier;
}

export function isFluxChain(chain: string): boolean {
  return chain === 'flux' || chain === 'fluxTestnet';
}

export function fluxTierFromAmount(satoshis: string | number): FluxNodeTier | null {
  const amt = typeof satoshis === 'string' ? parseInt(satoshis, 10) : satoshis;
  if (amt === FLUX_TIER_AMOUNTS.stratus) return 'stratus';
  if (amt === FLUX_TIER_AMOUNTS.nimbus) return 'nimbus';
  if (amt === FLUX_TIER_AMOUNTS.cumulus) return 'cumulus';
  return null;
}

export function isValidFluxCollateral(satoshis: string | number): boolean {
  return fluxTierFromAmount(satoshis) !== null;
}

export const FLUX_NODE_STATUS_LABELS: Record<FluxNodeStatus, string> = {
  offline: 'Offline',
  starting: 'Starting',
  started: 'Started',
  confirmed: 'Confirmed',
  dos: 'DOS',
};

export const FLUX_NODE_STATUS_COLORS: Record<FluxNodeStatus, string> = {
  offline: 'default',
  starting: 'processing',
  started: 'processing',
  confirmed: 'success',
  dos: 'error',
};
